import { Button, Descriptions, Modal, Space, Tag } from "antd";
import { useNavigate } from "react-router-dom";

export type CompanyUserKind =
  | "manager"
  | "employee"
  | "consultant"
  | "collaborator";

const KIND_LABEL: Record<CompanyUserKind, string> = {
  manager: "Manager",
  employee: "Employee",
  consultant: "Consultant",
  collaborator: "Collaborator",
};

const KIND_COLOR: Record<CompanyUserKind, string> = {
  manager: "blue",
  employee: "purple",
  consultant: "cyan",
  collaborator: "orange",
};

// Where the "Open on Users Page" button navigates per kind. The affiliates variants land on their own
// pages which are listed under /affiliates/*.
const KIND_PAGE: Record<CompanyUserKind, string> = {
  manager: "/users/managers",
  employee: "/users/employees",
  consultant: "/affiliates/consultants",
  collaborator: "/affiliates/collaborators",
};

export interface CompanyUserPreviewProps {
  readonly open: boolean;
  readonly kind: CompanyUserKind;
  readonly user:
    | {
        readonly uid?: string;
        readonly userName?: string;
        readonly firstName?: string;
        readonly middleName?: string;
        readonly lastName?: string;
        readonly email?: string;
        readonly phoneNumber?: string;
        readonly roleName?: string;
        readonly departmentName?: string;
        readonly companyName?: string;
      }
    | null;
  readonly onClose: () => void;
}

/**
 * Lightweight read-only user card for a row clicked inside the company panel. Deliberately simpler than
 * the full ManagerView / EmployeeView profile — those live under /users and keep the deep editing,
 * action log, lockout, etc. Here we just want to show "who is this" without navigating away.
 */
function CompanyUserPreviewModal(props: CompanyUserPreviewProps) {
  const navigate = useNavigate();

  const user = props.user;
  const fullName = user
    ? [user.firstName, user.middleName, user.lastName].filter(Boolean).join(" ")
    : "";

  return (
    <Modal
      open={props.open && !!user}
      onCancel={props.onClose}
      title={
        user && (
          <Space>
            <span>{fullName || user.userName || "User"}</span>
            <Tag color={KIND_COLOR[props.kind]}>{KIND_LABEL[props.kind]}</Tag>
          </Space>
        )
      }
      footer={
        <Space>
          <Button onClick={props.onClose}>Close</Button>
          <Button
            type="primary"
            onClick={() => {
              navigate(KIND_PAGE[props.kind]);
              props.onClose();
            }}
          >
            Open on Users Page
          </Button>
        </Space>
      }
    >
      {user && (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="User Name">
            {user.userName ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Role">
            {user.roleName ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Department">
            {user.departmentName ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Company">
            {user.companyName ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {user.email || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            {user.phoneNumber || "—"}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  );
}

export default CompanyUserPreviewModal;
